/**
 * Custom hook para manejar estado común de mapas
 * Centraliza lógica de carga, leyenda, vista y sincronización
 */

import { useState, useCallback, useMemo } from 'react';
import { calculateMapBounds, createMaskPolygon } from '../utils/mapUtils.js';
import { useMapSync } from '../components/forecast_metrics-components/maps/map_syncro.js';

/**
 * Hook personalizado para gestionar estado común de mapas
 * @param {string} layerType - Tipo de capa para calcular bounds
 * @returns {Object} Estado y funciones para manejar mapas
 */
export const useMapState = (layerType) => {
  // Estados básicos
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  const [hasData, setHasData] = useState(true);

  // Sincronización de mapas
  const { mapState, updateMapState } = useMapSync();

  // Cálculo de bounds y máscara
  const rectBounds = useMemo(() => calculateMapBounds(layerType), [layerType]);
  const maskPolygon = useMemo(() => createMaskPolygon(rectBounds), [rectBounds]);

  // Handlers
  const handleToggleLegend = useCallback(() => {
    setShowLegend(prev => !prev);
  }, []);

  const handleResetView = useCallback(() => {
    if (map && rectBounds) {
      map.fitBounds(rectBounds);
    }
  }, [map, rectBounds]);

  const handleMapCreated = useCallback((mapInstance) => {
    setMap(mapInstance);
    mapInstance.fitBounds(rectBounds);
  }, [rectBounds]);

  return {
    // Estados
    map,
    loading,
    showLegend,
    hasData,
    mapState,
    rectBounds,
    maskPolygon,
    
    // Setters
    setLoading,
    setHasData,
    setShowLegend,

    // Handlers
    handleToggleLegend,
    handleResetView,
    handleMapCreated,
    updateMapState,
  };
};
